import React from "react";
import * as Styles from "../../../../../common-deprecated/styles/v2/toyota/custom/SelectPane";
import { useCompareV2Selector } from "../../../redux/store";
import type { FlattenedCarType } from "../../../utils/compareTypes";
import Icon from "../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { IconWrapper } from "../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import { CompareV2Label, useCompareV2OptionalLabel } from "../../../utils/labels";
import { isModelCodeNewGeneration } from "../../../utils/utils";
import { isRTL } from "../../../../../common-deprecated/settings/utils/commonSettingUtils";
import type { ModelGradeBodyTypeIdsType } from "../../../../../common-deprecated/features/compare/utils/newCarCompareTypes";

type GradePanelPropsType = {
    setSelectedCar: (car: ModelGradeBodyTypeIdsType) => void;
    selectedCar: ModelGradeBodyTypeIdsType;
    clearSelection: () => void;
};

const GradePanel = (props: GradePanelPropsType): JSX.Element => {
    const { selectedCar, setSelectedCar, clearSelection } = props;

    const availableCarsData = useCompareV2Selector(({ newCarCompare }) => newCarCompare.available);
    const settings = useCompareV2Selector((state) => state.commonSettings);
    const selectedGrades = useCompareV2Selector(({ newCarCompare }) =>
        newCarCompare.grades.map(({ gradeId, bodyTypeId, submodelId }) => submodelId + gradeId + bodyTypeId),
    );
    const model = availableCarsData.find((m) => m.modelCode === selectedCar.model);
    const isNewGeneration = isModelCodeNewGeneration(settings, model?.modelCode || "");
    const nextGenLabel = useCompareV2OptionalLabel("compareSelectGradeNextGen", { model: model?.modelName });

    // The grades and submodels are flattened here so it will be easier to map through them. Toyota doesn't need seperate submodels
    const availableGrades = model?.subModels
        .reduce((modelAccumulator: FlattenedCarType[], subModel) => {
            return subModel.grades.reduce((subModelAccumulator: FlattenedCarType[], grade) => {
                return grade.bodyTypes.reduce((gradeAccumulator: FlattenedCarType[], bodyType) => {
                    return gradeAccumulator.concat({
                        gradeId: grade.gradeId,
                        bodyTypeId: bodyType.bodyTypeId,
                        subModelId: subModel.subModelId,
                        subModelName: subModel.subModelName,
                        bodyTypeName: bodyType.bodyTypeName,
                        gradeName: grade.gradeName,
                    });
                }, subModelAccumulator);
            }, modelAccumulator);
        }, [])
        .filter(({ gradeId, bodyTypeId, subModelId }) => !selectedGrades.includes(subModelId + gradeId + bodyTypeId));

    return (
        <Styles.Panel id={`${selectedCar.model}-pane`} aria-labelledby={`${selectedCar.model}-header`}>
            {model?.modelName && (
                <Styles.Header onClick={clearSelection}>
                    <Styles.HeaderButton>
                        <IconWrapper iconColor="primaryBlack">
                            <Icon variant={isRTL(settings) ? "arrowRight" : "arrowLeft"} />
                        </IconWrapper>
                        <CompareV2Label label="compareSelectBack" />
                    </Styles.HeaderButton>
                    <Styles.HeaderLabel>
                        {isNewGeneration && nextGenLabel ? nextGenLabel : model.modelName}
                    </Styles.HeaderLabel>
                </Styles.Header>
            )}
            <Styles.PanelContent>
                <Styles.PanelScroll>
                    {availableGrades &&
                        availableGrades.map(
                            ({ gradeId, bodyTypeId, gradeName, bodyTypeName, subModelId, subModelName }) => (
                                /* Improvement possible to change the next focus on the confirm button when car is selected + TAB */
                                <Styles.PanelItem
                                    key={gradeId + bodyTypeId + subModelName}
                                    selected={selectedCar.gradeId === gradeId && selectedCar.bodyTypeId === bodyTypeId}
                                    onClick={() =>
                                        setSelectedCar({
                                            ...selectedCar,
                                            gradeId,
                                            bodyTypeId,
                                            submodelId: subModelId,
                                        })
                                    }
                                    aria-pressed={
                                        selectedCar.gradeId === gradeId && selectedCar.bodyTypeId === bodyTypeId
                                    }
                                >
                                    {`${subModelName || ""} ${gradeName} ${bodyTypeName}`.trim()}
                                </Styles.PanelItem>
                            ),
                        )}
                </Styles.PanelScroll>
            </Styles.PanelContent>
        </Styles.Panel>
    );
};

export default GradePanel;
